/**
 * S3 Upload Progress Component
 *
 * Displays real-time progress for S3 uploads with:
 * - Collapsible MUI Card container
 * - DataGrid showing upload queue with individual file progress
 * - Individual file progress bars with speed/ETA
 * - Aggregate metrics (total files, percentage, upload speed)
 * - Control buttons placeholder (wired in subtask 6.2)
 * - Auto-hide when queue is empty
 */

import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Divider,
  useTheme,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Timer as TimerIcon,
  Speed as SpeedIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Close as CancelIcon,
  Refresh as RetryIcon,
} from "@mui/icons-material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import type {
  UploadJob,
  UploadItem,
  UploadProgressMetrics,
} from "../../types/s3";
import {
  formatFileSize,
  formatUploadSpeed,
  formatTimeRemaining,
} from "../../utils/fileValidator";

// ============================================================================
// Type Definitions
// ============================================================================

interface S3UploadProgressProps {
  /** Upload jobs to display */
  uploadJobs: UploadJob[];
  /** Whether to show the component (auto-hide when queue is empty) */
  show?: boolean;
  /** Custom title for the component */
  title?: string;
  /** Callback when pause button is clicked (wired in 6.2) */
  onPause?: (jobId: string, itemId: string) => void;
  /** Callback when resume button is clicked (wired in 6.2) */
  onResume?: (jobId: string, itemId: string) => void;
  /** Callback when cancel button is clicked (wired in 6.2) */
  onCancel?: (jobId: string, itemId: string) => void;
  /** Callback when retry button is clicked (wired in 6.2) */
  onRetry?: (jobId: string, itemId: string) => void;
  /** Callback to clear completed uploads (wired in 6.2) */
  onClearCompleted?: () => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate aggregate metrics across all upload jobs
 */
const calculateAggregateMetrics = (
  jobs: UploadJob[]
): UploadProgressMetrics => {
  const now = new Date();
  let totalFiles = 0;
  let filesUploaded = 0;
  let filesFailed = 0;
  let totalBytes = 0;
  let bytesUploaded = 0;
  let startTime = now;

  for (const job of jobs) {
    totalFiles += job.progress.totalFiles;
    filesUploaded += job.progress.filesUploaded;
    filesFailed += job.progress.filesFailed;
    totalBytes += job.progress.totalBytes;
    bytesUploaded += job.progress.bytesUploaded;

    if (job.progress.startTime < startTime) {
      startTime = job.progress.startTime;
    }
  }

  const elapsedMs = now.getTime() - startTime.getTime();
  const elapsedSeconds = elapsedMs / 1000;
  const uploadSpeed =
    elapsedSeconds > 0 ? Math.max(0, bytesUploaded / elapsedSeconds) : 0;

  let estimatedTimeRemaining: number | null = null;
  if (uploadSpeed > 0 && bytesUploaded < totalBytes) {
    estimatedTimeRemaining = Math.ceil(
      (totalBytes - bytesUploaded) / uploadSpeed
    );
  }

  return {
    totalFiles,
    filesUploaded,
    filesFailed,
    totalBytes,
    bytesUploaded,
    uploadSpeed,
    estimatedTimeRemaining,
    percentage:
      totalBytes > 0
        ? Math.min(100, Math.round((bytesUploaded / totalBytes) * 100))
        : 0,
    startTime,
    currentTime: now,
  };
};

/**
 * Get status color for a file
 */
const getStatusColor = (
  status: string
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "warning"
  | "info"
  | "success" => {
  switch (status) {
    case "pending":
      return "default";
    case "uploading":
      return "primary";
    case "completed":
      return "success";
    case "failed":
      return "error";
    case "cancelled":
      return "warning";
    case "paused":
      return "info";
    default:
      return "default";
  }
};

// ============================================================================
// Progress Bar Component
// ============================================================================

interface FileProgressBarProps {
  item: UploadItem;
}

const FileProgressBar: React.FC<FileProgressBarProps> = ({ item }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.file.name}
          </Typography>
          <Typography variant="caption" fontWeight="bold" sx={{ ml: 1 }}>
            {item.progress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={item.progress}
          sx={{
            height: 6,
            borderRadius: 1,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.1)",
          }}
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const S3UploadProgress: React.FC<S3UploadProgressProps> = ({
  uploadJobs,
  show = true,
  title = "Upload Progress",
  onPause,
  onResume,
  onCancel,
  onRetry,
  onClearCompleted,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(true);

  // Flatten all files from all jobs for DataGrid display (must be before conditional)
  const allFiles: Array<UploadItem & { jobId: string }> = useMemo(() => {
    const files: Array<UploadItem & { jobId: string }> = [];
    for (const job of uploadJobs) {
      for (const file of job.files) {
        files.push({ ...file, jobId: job.jobId });
      }
    }
    return files;
  }, [uploadJobs]);

  // Calculate aggregate metrics (must be before conditional)
  const aggregateMetrics = useMemo(
    () => calculateAggregateMetrics(uploadJobs),
    [uploadJobs]
  );

  // Count completed jobs (must be before conditional)
  const completedCount = useMemo(
    () =>
      uploadJobs.filter(
        (job) =>
          job.status === "completed" ||
          job.status === "failed" ||
          job.status === "cancelled"
      ).length,
    [uploadJobs]
  );

  // Calculate whether to show the component
  const hasActiveUploads = uploadJobs.length > 0;
  const shouldShow = show && hasActiveUploads;

  if (!shouldShow) {
    return null;
  }

  // DataGrid columns configuration
  const columns: GridColDef[] = [
    {
      field: "file",
      headerName: "File Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<any, UploadItem>) => (
        <Typography
          variant="body2"
          noWrap
          title={(params.row as any).file.name}
        >
          {(params.row as any).file.name}
        </Typography>
      ),
    },
    {
      field: "progress",
      headerName: "Progress",
      width: 550,
      renderCell: (params: GridRenderCellParams<any, UploadItem>) => (
        <Box sx={{ width: "100%", pr: 1 }}>
          <FileProgressBar item={params.row as any} />
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params: GridRenderCellParams<any, UploadItem>) => (
        <Chip
          size="small"
          label={(params.row as any).status}
          color={getStatusColor((params.row as any).status)}
          variant="outlined"
        />
      ),
    },
  ];

  return (
    <Card
      sx={{
        mb: 2,
        boxShadow: theme.palette.mode === "dark" ? 2 : 1,
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(30, 30, 30, 0.9)"
            : "background.paper",
      }}
    >
      <CardHeader
        avatar={<CloudUploadIcon color="primary" />}
        title={title}
        subheader={`${uploadJobs.length} job${
          uploadJobs.length !== 1 ? "s" : ""
        } (${aggregateMetrics.filesUploaded}/${
          aggregateMetrics.totalFiles
        } files)`}
        action={
          <IconButton
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <ExpandMoreIcon
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shortest,
                }),
              }}
            />
          </IconButton>
        }
        sx={{
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(66, 165, 245, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)"
              : "linear-gradient(135deg, rgba(66, 165, 245, 0.05) 0%, rgba(33, 150, 243, 0.02) 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
          pt: 2,
        }}
      />

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          {/* Aggregate Metrics Section */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Overall Progress
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="primary">
                {aggregateMetrics.percentage}%
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={aggregateMetrics.percentage}
              sx={{
                height: 10,
                borderRadius: 1,
                mb: 2,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.1)",
              }}
            />

            {/* Metrics Grid */}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.02)",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <CheckCircleIcon
                      sx={{ fontSize: "1.2rem", color: "success.main" }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Files
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {aggregateMetrics.filesUploaded}/
                    {aggregateMetrics.totalFiles}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.02)",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Data
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {formatFileSize(aggregateMetrics.bytesUploaded)} /{" "}
                    {formatFileSize(aggregateMetrics.totalBytes)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.02)",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <SpeedIcon
                      sx={{ fontSize: "1.2rem", color: "info.main" }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Speed
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {formatUploadSpeed(aggregateMetrics.uploadSpeed)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.02)",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <TimerIcon
                      sx={{ fontSize: "1.2rem", color: "warning.main" }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      ETA
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {formatTimeRemaining(
                      aggregateMetrics.estimatedTimeRemaining
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Files DataGrid */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Upload Queue ({allFiles.length} files)
            </Typography>
            <Box
              sx={{
                height: Math.min(400, Math.max(200, allFiles.length * 50)),
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(0,0,0,0.2)"
                    : "rgba(0,0,0,0.02)",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                "& .MuiDataGrid-root": {
                  border: "none",
                  backgroundColor: "transparent",
                },
                "& .MuiDataGrid-cell": {
                  borderColor: theme.palette.divider,
                  padding: "8px 12px",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 600,
                },
              }}
            >
              <DataGrid
                rows={allFiles}
                columns={columns}
                pageSizeOptions={[5, 10, 25]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10, page: 0 },
                  },
                }}
                disableRowSelectionOnClick
                density="compact"
                hideFooterSelectedRowCount
              />
            </Box>
          </Box>

          {/* Control Buttons Section */}
          {completedCount > 0 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Typography variant="caption" color="textSecondary">
                {completedCount} completed
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => onClearCompleted?.()}
              >
                Clear Completed
              </Button>
            </Stack>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default S3UploadProgress;
